import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";

const TSHIRT = "/launch/tshirt.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["S", "M", "L", "XL", "XXL"];
const COLORS = ["#F59E0B", "#E8266F", "#7C3AED", "#FFFFFF"];

export default function Launch() {
  // phases: "anticipation" -> "revealing" -> "settled"
  const [phase, setPhase] = useState("anticipation");
  const [size, setSize] = useState("M");
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const contInterval = useRef(null);
  const timers = useRef([]);

  // Big celebratory burst
  const bigBurst = () => {
    const fire = (o) => confetti({ colors: COLORS, disableForReducedMotion: true, ...o });
    fire({ particleCount: 140, spread: 90, startVelocity: 55, origin: { y: 0.42 } });
    setTimeout(() => fire({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.55 } }), 120);
    setTimeout(() => fire({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.55 } }), 120);
    setTimeout(() => fire({ particleCount: 120, spread: 120, startVelocity: 35, decay: 0.9, scalar: 1.2, origin: { y: 0.35 } }), 260);
  };

  // Gentle continuous falling confetti (keeps the celebration alive)
  const startContinuous = () => {
    if (contInterval.current) return;
    contInterval.current = setInterval(() => {
      confetti({
        colors: COLORS,
        disableForReducedMotion: true,
        particleCount: 5,
        startVelocity: 0,
        gravity: 0.55,
        ticks: 260,
        spread: 60,
        scalar: 0.9,
        drift: (Math.random() - 0.5) * 1.2,
        origin: { x: Math.random(), y: -0.05 },
      });
    }, 550);
  };

  const handleSettle = () => {
    setPhase("settled");
    bigBurst();
    startContinuous();
  };

  // Anticipation build-up, then trigger the reveal (mount the shirt)
  useEffect(() => {
    const t = setTimeout(() => setPhase("revealing"), 2600);
    timers.current.push(t);
    return () => {
      timers.current.forEach(clearTimeout);
      if (contInterval.current) clearInterval(contInterval.current);
    };
  }, []);

  const revealed = phase === "revealing" || phase === "settled";
  const settled = phase === "settled";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.trim() && !form.phone.trim()) return toast.error("Add an email or phone so we can reach you.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/preorders`, {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        size, quantity: Number(form.quantity) || 1,
      });
      setDone(true);
      bigBurst();
      toast.success("Pre-order received! We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="launch-page"
      className="relative w-full overflow-hidden bg-[#050106] flex flex-col items-center justify-center px-5 pt-28 pb-24"
      style={{ perspective: "1500px", minHeight: "100vh" }}
    >
      {/* Deep vignette */}
      <div className="pointer-events-none absolute inset-0 z-0" style={{ background: "radial-gradient(circle at 50% 38%, rgba(0,0,0,0) 26%, rgba(0,0,0,0.9) 92%)" }} />

      {/* Volumetric spotlight BEAM from top (the stage light) */}
      <motion.div
        aria-hidden
        data-testid="launch-spotlight"
        className="pointer-events-none absolute left-1/2 top-0 z-0 -translate-x-1/2"
        style={{
          width: "min(90vw, 900px)",
          height: "78vh",
          clipPath: "polygon(42% 0, 58% 0, 100% 100%, 0% 100%)",
          background: "linear-gradient(to bottom, rgba(255,244,224,0.55), rgba(232,38,111,0.16) 45%, rgba(124,58,237,0.05) 75%, rgba(0,0,0,0) 100%)",
          filter: "blur(14px)",
        }}
        animate={
          settled
            ? { opacity: 0.95, scaleX: 1, filter: ["hue-rotate(0deg) blur(14px)", "hue-rotate(360deg) blur(14px)"] }
            : revealed
            ? { opacity: 0.85, scaleX: 1, filter: ["hue-rotate(0deg) blur(14px)", "hue-rotate(360deg) blur(14px)"] }
            : { opacity: [0.2, 0.55, 0.2], scaleX: [0.85, 1, 0.85], filter: ["hue-rotate(0deg) blur(14px)", "hue-rotate(360deg) blur(14px)"] }
        }
        transition={{
          filter: { duration: 6, repeat: Infinity, ease: "linear" },
          opacity: settled || revealed ? { duration: 1.1, ease: "easeOut" } : { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
          scaleX: settled || revealed ? { duration: 1.1, ease: "easeOut" } : { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
        }}
      />

      {/* Bright light source at the top of the beam */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-6vh] z-0 h-40 w-40 -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(255,250,240,0.9), rgba(232,38,111,0.3) 55%, rgba(0,0,0,0) 72%)", filter: "blur(10px)" }}
        animate={revealed ? { opacity: 1, scale: 1.1, filter: ["hue-rotate(0deg) blur(10px)", "hue-rotate(360deg) blur(10px)"] } : { opacity: [0.4, 0.85, 0.4], scale: [0.9, 1.05, 0.9], filter: ["hue-rotate(0deg) blur(10px)", "hue-rotate(360deg) blur(10px)"] }}
        transition={{ filter: { duration: 6, repeat: Infinity, ease: "linear" }, opacity: revealed ? { duration: 1 } : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }, scale: revealed ? { duration: 1 } : { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }}
      />

      {/* Floor glow pool where the shirt lands */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute left-1/2 z-0 h-28 w-[min(52vw,560px)] -translate-x-1/2 rounded-[100%]"
        style={{ top: "58%", background: "radial-gradient(ellipse at center, rgba(232,38,111,0.55), rgba(124,58,237,0.2) 55%, rgba(0,0,0,0) 75%)", filter: "blur(26px)" }}
        initial={{ opacity: 0, scaleX: 0.3 }}
        animate={settled ? { opacity: 0.8, scaleX: 1 } : { opacity: 0, scaleX: 0.3 }}
        transition={{ duration: 1.1, delay: settled ? 0.2 : 0 }}
      />

      {/* ANTICIPATION overlay */}
      <AnimatePresence>
        {phase === "anticipation" && (
          <motion.div
            key="anticipation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
            data-testid="launch-anticipation"
            className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center"
          >
            <motion.p
              animate={{ opacity: [0.35, 1, 0.35], letterSpacing: ["0.3em", "0.42em", "0.3em"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="font-display text-xs md:text-sm uppercase text-white/70"
            >
              The Unveiling
            </motion.p>
            <motion.div
              className="mt-8 h-16 w-16 rounded-full"
              style={{ border: "2px solid rgba(232,38,111,0.5)", boxShadow: "0 0 30px rgba(232,38,111,0.5)" }}
              animate={{ scale: [1, 1.35, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.p
              animate={{ opacity: [0, 0.6, 0] }}
              transition={{ duration: 2.6, repeat: Infinity }}
              className="mt-8 font-serif italic text-lg text-white/40"
            >
              something the movement will wear with pride…
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* T-shirt: mounts on reveal, drops from above with a 3D spin */}
      {revealed && (
        <motion.div
          className="relative z-10"
          style={{ transformStyle: "preserve-3d" }}
          initial={{ y: -480, opacity: 0, rotateY: 420, rotateX: -16, scale: 0.82 }}
          animate={{ y: 0, opacity: 1, rotateY: 0, rotateX: 0, scale: 1 }}
          transition={{ duration: 2.2, ease: [0.18, 0.9, 0.25, 1], opacity: { duration: 1.1 } }}
          onAnimationComplete={handleSettle}
        >
          <motion.div
            animate={settled ? { y: [0, -12, 0] } : {}}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformStyle: "preserve-3d" }}
          >
            <motion.img
              src={TSHIRT}
              alt="Choices & Decisions launch t-shirt"
              data-testid="launch-tshirt"
              draggable={false}
              className="h-[58vh] max-h-[600px] w-auto select-none object-contain"
              style={{ filter: "drop-shadow(0 40px 60px rgba(0,0,0,0.8))" }}
              animate={settled ? { filter: ["drop-shadow(0 40px 60px rgba(0,0,0,0.8))", "drop-shadow(0 22px 55px rgba(232,38,111,0.45))", "drop-shadow(0 40px 60px rgba(0,0,0,0.8))"] } : {}}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Text + form reveal */}
      <div className="relative z-10 mt-8 flex flex-col items-center text-center w-full">
        <AnimatePresence>
          {settled && (
            <>
              <motion.p
                data-testid="launch-kicker"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0, letterSpacing: "0.34em" }}
                transition={{ duration: 1, delay: 0.2 }}
                className="font-display text-[10px] md:text-xs uppercase text-white/45"
              >
                The Movement Wears It Best
              </motion.p>

              <motion.h1
                data-testid="launch-title"
                initial={{ opacity: 0, y: 26, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="mt-4 font-serif font-light text-5xl sm:text-6xl md:text-7xl leading-[0.95] text-transparent bg-clip-text"
                style={{
                  backgroundImage: "linear-gradient(100deg, #F59E0B 0%, #E8266F 45%, #7C3AED 100%)",
                  filter: "drop-shadow(0 0 22px rgba(232,38,111,0.55)) drop-shadow(0 0 46px rgba(124,58,237,0.35))",
                }}
              >
                Choices &amp; Decisions
              </motion.h1>

              <motion.div
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
                className="mt-6 h-[2px] w-52 origin-center rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, #E8266F, #7C3AED, transparent)", boxShadow: "0 0 18px rgba(232,38,111,0.7)" }}
              />

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.25 }}
                className="mt-9 w-full max-w-md"
              >
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="thanks"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      data-testid="launch-thankyou"
                      className="rounded-2xl border border-white/15 bg-white/[0.04] px-8 py-10 text-center"
                    >
                      <p className="font-serif text-3xl text-white">You're on the list! 🎉</p>
                      <p className="mt-3 font-sans text-sm text-white/60">Your pre-order for a size {size} tee is in. We'll reach out with payment &amp; pickup details soon.</p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={submit}
                      data-testid="launch-preorder-form"
                      className="rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-sm px-6 py-6 text-left"
                    >
                      <p className="font-display text-[10px] uppercase tracking-[0.24em] text-white/45 mb-3 text-center">Reserve Yours — Pre-order</p>
                      <div className="mb-4">
                        <p className="font-display text-[10px] uppercase tracking-[0.16em] text-white/50 mb-2">Size</p>
                        <div className="flex flex-wrap gap-2" data-testid="launch-size-selector">
                          {SIZES.map((s) => (
                            <button
                              type="button" key={s} onClick={() => setSize(s)} data-testid={`launch-size-${s}`}
                              className={`h-10 w-12 rounded-lg font-display text-sm font-bold transition-all ${size === s ? "text-white" : "border border-white/20 text-white/60 hover:border-white/50"}`}
                              style={size === s ? { backgroundImage: "linear-gradient(100deg, #F59E0B, #E8266F 55%, #7C3AED)" } : {}}
                            >{s}</button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <input data-testid="launch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="rounded-lg bg-white/[0.06] border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none focus:border-magenta" />
                        <div className="grid grid-cols-2 gap-3">
                          <input data-testid="launch-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-lg bg-white/[0.06] border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none focus:border-magenta" />
                          <input data-testid="launch-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-lg bg-white/[0.06] border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none focus:border-magenta" />
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="font-display text-[10px] uppercase tracking-[0.16em] text-white/50">Qty</label>
                          <input type="number" min="1" max="50" data-testid="launch-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-20 rounded-lg bg-white/[0.06] border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-magenta" />
                        </div>
                      </div>
                      <button
                        type="submit" disabled={submitting} data-testid="launch-submit"
                        className="mt-5 w-full rounded-full px-8 py-4 font-display text-xs uppercase tracking-[0.16em] font-bold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                        style={{ backgroundImage: "linear-gradient(100deg, #F59E0B, #E8266F 55%, #7C3AED)", boxShadow: "0 0 28px rgba(232,38,111,0.45)" }}
                      >{submitting ? "Reserving…" : "Claim Yours"}</button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
